import Papa from "papaparse";

export interface BudgetData {
  match_key: string;
  ลำดับ: string;
  "ชื่อ อปท.": string;
  รท: string;
  อำเภอ: string;
  จังหวัด: string;
  พื้นที่: string;
  ประชากร: string;
  จัดเก็บเอง: string;
  รัฐจัดสรร: string;
  เงินอุดหนุน: string;
  รายได้รวม: string;
}

export interface ParsedBudgetData {
  matchKey: string;
  order: number;
  name: string;
  code: number;
  district: string;
  province: string;
  area: number; // พื้นที่ in sq km
  population: number; // ประชากร
  selfCollected: number; // จัดเก็บเอง in million baht
  stateAllocated: number; // รัฐจัดสรร in million baht
  subsidies: number; // เงินอุดหนุน in million baht
  totalRevenue: number; // รายได้รวม in million baht
}

// Interface for exclusion list items
interface ExcludedOBT {
  tambon: string;
  amphoe: string;
  province: string;
}

// Cache for budget data by year
const budgetCache: { [year: number]: ParsedBudgetData[] } = {};

// Cache for exclusion list
let exclusionListCache: ExcludedOBT[] | null = null;

// Available years (Buddhist calendar)
export const AVAILABLE_YEARS = [2560, 2561, 2562, 2563, 2564, 2565, 2566, 2567];

// Convert Buddhist year to display format
export const formatYear = (buddhistYear: number): string => {
  return `${buddhistYear} (${buddhistYear - 543})`;
};

// Normalize text for comparison
const normalizeText = (text: string): string => 
  text?.replace(/\s+/g, '').replace(/จังหวัด|อำเภอ|ตำบล|องค์การบริหารส่วนตำบล|อบต\./g, '').toLowerCase() || '';

// Load exclusion list of upgraded OBT
const loadExclusionList = async (): Promise<ExcludedOBT[]> => {
  if (exclusionListCache) {
    return exclusionListCache;
  }
  
  try {
    const response = await fetch(
      `${process.env.PUBLIC_URL}/data/upgraded-obt-exclusion.json`
    );
    if (!response.ok) {
      console.warn("Could not load exclusion list");
      return [];
    }
    const data = await response.json();
    const loadedList: ExcludedOBT[] = data.excludedOBT || [];
    exclusionListCache = loadedList;
    console.log(`Loaded ${loadedList.length} upgraded OBT to exclude from budget data`);
    return loadedList;
  } catch (error) {
    console.warn("Error loading exclusion list:", error);
    return [];
  }
};

// Check if an OBT should be excluded (upgraded to เทศบาล)
const isExcludedOBT = (
  name: string, 
  district: string, 
  province: string, 
  exclusionList: ExcludedOBT[]
): boolean => {
  const normalizedName = normalizeText(name);
  const normalizedDistrict = normalizeText(district);
  const normalizedProvince = normalizeText(province);
  
  return exclusionList.some(excluded => {
    const excludedTambon = normalizeText(excluded.tambon);
    const excludedAmphoe = normalizeText(excluded.amphoe);
    const excludedProvince = normalizeText(excluded.province);
    
    const nameMatch = normalizedName.includes(excludedTambon) || excludedTambon.includes(normalizedName);
    const districtMatch = normalizedDistrict.includes(excludedAmphoe) || excludedAmphoe.includes(normalizedDistrict);
    const provinceMatch = normalizedProvince.includes(excludedProvince) || excludedProvince.includes(normalizedProvince);
    
    return nameMatch && districtMatch && provinceMatch;
  });
};

// Parse a budget row to a structured object
const parseBudgetRow = (row: BudgetData): ParsedBudgetData => {
  return {
    matchKey: row.match_key || "",
    order: parseFloat(row.ลำดับ) || 0,
    name: row["ชื่อ อปท."] || "",
    code: parseFloat(row.รท) || 0,
    district: row.อำเภอ || "",
    province: row.จังหวัด || "",
    area: parseFloat(row.พื้นที่) || 0,
    population: parseFloat(row.ประชากร) || 0,
    selfCollected: parseFloat(row.จัดเก็บเอง) || 0,
    stateAllocated: parseFloat(row.รัฐจัดสรร) || 0,
    subsidies: parseFloat(row.เงินอุดหนุน) || 0,
    totalRevenue: parseFloat(row.รายได้รวม) || 0,
  };
};

// Load budget data for a specific year
export const loadBudgetData = async (
  year: number
): Promise<ParsedBudgetData[]> => {
  // Check cache first
  if (budgetCache[year]) {
    return budgetCache[year];
  }

  // Validate year
  if (!AVAILABLE_YEARS.includes(year)) {
    throw new Error(`Budget data not available for year ${year}`);
  }

  try {
    // Load exclusion list
    const exclusionList = await loadExclusionList();
    
    const response = await fetch(
      `${process.env.PUBLIC_URL}/data/yearly-budget/budget_${year}.csv`
    );

    if (!response.ok) {
      throw new Error(`Failed to load budget data for year ${year}`);
    }

    const csvText = await response.text();

    return new Promise((resolve, reject) => {
      Papa.parse<BudgetData>(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results: any) => {
          const parsedData = results.data.map(parseBudgetRow);
          
          // Filter out upgraded OBT (now เทศบาล)
          const filteredData = parsedData.filter((item: ParsedBudgetData) => {
            const excluded = isExcludedOBT(item.name, item.district, item.province, exclusionList);
            if (excluded) {
              console.log(`Excluding upgraded OBT from budget: ${item.name} (${item.district}, ${item.province})`);
            }
            return !excluded;
          });
          
          console.log(`Budget data for ${year}: ${filteredData.length} OBT (excluded ${parsedData.length - filteredData.length} upgraded)`);
          
          budgetCache[year] = filteredData;
          resolve(filteredData);
        },
        error: (error: any) => {
          reject(new Error(`Error parsing CSV: ${error.message}`));
        },
      });
    });
  } catch (error) {
    console.error(`Error loading budget data for year ${year}:`, error);
    throw error;
  }
};

// Find budget data for a specific match key
export const findBudgetByMatchKey = (
  budgetData: ParsedBudgetData[],
  matchKey: string
): ParsedBudgetData | undefined => {
  return budgetData.find((row) => row.matchKey === matchKey);
};

// Get budget data for all available years for a specific match key
export const getBudgetHistoryByMatchKey = async (
  matchKey: string
): Promise<{ year: number; data: ParsedBudgetData }[]> => {
  const history: { year: number; data: ParsedBudgetData }[] = [];

  for (const year of AVAILABLE_YEARS) {
    try {
      const budgetData = await loadBudgetData(year);
      const data = findBudgetByMatchKey(budgetData, matchKey);
      if (data) {
        history.push({ year, data });
      }
    } catch (error) {
      console.error(`Error loading budget data for year ${year}:`, error);
    }
  }

  return history;
};
